import { useEffect, useMemo, useState } from 'react';

import { useParams, useSearchParams } from 'react-router';

import {
  usePrototypeStatistics,
  useReportConfig,
} from '@app-velocity/modules/simulation-report';

import {
  useGetScenarioAiLatestReviewQuery,
  useGetSharedPrototypeFullSummaryQuery,
  useScenarioDetailsContext,
  useGetScenarioReviewsByShareTokenQuery,
  useSharedScenarioEvents,
  useUserSimulationControl,
} from '@app-velocity/modules/scenarios';

import { Skeleton } from '@app-velocity/ui';

import { TAiReviewDto } from '@app-velocity/av-api-v2';

import { SimulationDetails } from './components/SimulationDetails';
import { UserSimulationWidgetContext } from './context/UserSimulationWidgetContext';
import { useSavePlugin } from '../../../../../../hooks';
import { WidgetTabs } from './components/WidgetTabs';
import { Requirements } from './components/Requirements';

export const UserSimulationWidget = () => {
  const { teamId, workspaceId, scenarioId } = useParams<{
    teamId: string;
    workspaceId: string;
    scenarioId: string;
  }>();

  const { start } = useUserSimulationControl();
  const [refreshReview, setRefreshReview] = useState<boolean>(false);

  const { scenario } = useScenarioDetailsContext();
  const { data: latestReview, isLoading: isLatestReviewLoading } =
    useGetScenarioAiLatestReviewQuery(workspaceId, scenarioId, refreshReview);

  const {
    data: summary,
    refetch: refetchSummary,
    isLoading: isSummaryLoading,
  } = useGetSharedPrototypeFullSummaryQuery(scenario.shareToken);

  const { data: humanReviews, isLoading: areHumanReviewsLoading } =
    useGetScenarioReviewsByShareTokenQuery(scenario.shareToken || '');

  const { data: humanEvents } = useSharedScenarioEvents(
    scenario.shareToken || ''
  );

  const { handlePluginModal } = useSavePlugin();

  const [params, setParams] = useSearchParams();

  useEffect(() => {
    const action = params.get('action');
    if (!action) return;

    if (action === 'start') {
      start(teamId!, workspaceId!, scenario.id!, 'default', 1, true);
      setParams({});
    }
  }, [params]);

  useEffect(() => {
    if (!latestReview || latestReview.reviewStatus !== 'COMPLETED') return;

    setTimeout(() => {
      handlePluginModal();
    }, 1500);
  }, [handlePluginModal, latestReview]);

  useEffect(() => {
    if (!latestReview) return;

    if (latestReview.reviewStatus === 'IN_PROGRESS') {
      setRefreshReview(true);

      return;
    }

    refetchSummary();
    setRefreshReview(false);
  }, [latestReview]);

  const reportConfig = useReportConfig(workspaceId || '', scenarioId || '');
  const statistics = usePrototypeStatistics(
    scenario,
    summary,
    humanReviews || undefined,
    humanEvents
  );

  const parsedStatus: TAiReviewDto['reviewStatus'] | null = useMemo(() => {
    if (latestReview?.reviewStatus === 'IN_PROGRESS') return 'IN_PROGRESS';
    if (!statistics) return null;

    const successes = statistics.simulatedReviews.succeded;
    const failures = statistics.simulatedReviews.failed;

    if (successes + failures > 1) {
      const ratio = successes / (failures + successes);
      if (ratio >= 0.7) return 'COMPLETED';

      return 'CANCELED';
    }

    if (successes === 1) return 'COMPLETED';
    if (failures === 1) return 'CANCELED';

    return null;
  }, [latestReview, statistics]);

  const status = useMemo(() => {
    switch (parsedStatus) {
      case 'IN_PROGRESS':
        return 'pending';
      case 'CANCELED':
        return 'error';
      case 'COMPLETED':
        return 'success';
      default:
        return null;
    }
  }, [parsedStatus]);

  const displayRequirements =
    (statistics?.simulatedReviews.failed || 0) +
      (statistics?.simulatedReviews.succeded || 0) +
      (statistics?.humanReviews.succeded || 0) +
      (statistics?.humanReviews.failed || 0) >
    0;

  if (
    isLatestReviewLoading ||
    isSummaryLoading ||
    areHumanReviewsLoading ||
    reportConfig.isLoading
  )
    return <Skeleton margin="0" height={140} />;

  return (
    <UserSimulationWidgetContext.Provider
      value={{
        parsedStatus,
        latestReview,
        teamId,
        workspaceId,
        scenarioId,
        shareToken: scenario.shareToken || '',
        summary,
        config: reportConfig.config,
        statistics,
      }}
    >
      <WidgetTabs
        tabs={[
          {
            title: 'prototypes:result',
            status,
            component: <SimulationDetails />,
            active: true,
          },
          {
            title: 'prototypes:requirements',
            status:
              statistics?.meetsRequirements === true ? 'success' : 'error',
            component: <Requirements />,
            active: displayRequirements,
          },
        ]}
      />
    </UserSimulationWidgetContext.Provider>
  );
};
